import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { multiSiteApi, TenantSite } from '../api/multi-site-api';
import { esPlanSinSitios, olvidarTenantSinSitios, recordarTenantSinSitios, tenantSinSitios } from './sitios-sin-plan';

interface SelectedSiteContextValue {
  sites: TenantSite[];
  selectedSiteId: string | null;
  selectedSite: TenantSite | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  selectSite: (siteId: string | null) => void;
}

const LOCAL_STORAGE_KEY = 'inbomarket_selected_site_id';

const SelectedSiteContext = createContext<SelectedSiteContextValue | null>(null);

interface SelectedSiteProviderProps {
  children: ReactNode;
  /**
   * GH#1201 — `false` = nadie va a usar la lista (SUPER_ADMIN, panel `/admin`, sin tenant): no se pide `GET /multi-site/sites`, que ahí da 403.
   * Por defecto `true`, como antes.
   */
  enabled?: boolean;
  /**
   * pmo #23485 - el tenant del usuario. Con él, un `403 PLAN_FEATURE_MISSING` («tu plan no trae soulbuilder») se lee como «este tenant no tiene sitios»,
   * sin error visible, y no se vuelve a pedir en la sesión. Sin él (undefined) el proveedor se comporta como antes.
   */
  tenantId?: string | null;
}

export const SelectedSiteProvider = ({ children, enabled = true, tenantId }: SelectedSiteProviderProps) => {
  const [sites, setSites] = useState<TenantSite[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LOCAL_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const olvidarSeleccion = useCallback(() => {
    setSelectedSiteId(null);
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  /**
   * `usarMemoria`: la carga AUTOMÁTICA del layout respeta lo recordado (un tenant sin soulbuilder no se vuelve a consultar en la sesión); un `refresh()`
   * pedido por quien consume (p. ej. tras crear un sitio) SIEMPRE pregunta de verdad, y si el servidor responde bien, olvida el «sin sitios».
   */
  const cargar = useCallback(
    async (usarMemoria: boolean) => {
      if (usarMemoria && tenantSinSitios(tenantId)) {
        setSites([]);
        setError(null);
        setLoading(false);
        if (selectedSiteId) olvidarSeleccion();
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await multiSiteApi.list();
        olvidarTenantSinSitios(tenantId);
        setSites(data);
        if (selectedSiteId && !data.some((s) => s.id === selectedSiteId)) olvidarSeleccion();
      } catch (e: any) {
        if (esPlanSinSitios(e)) {
          // «tu plan no incluye soulbuilder» = este tenant no tiene sitios. No es un fallo: lista vacía, sin error, y no se repite.
          recordarTenantSinSitios(tenantId);
          setSites([]);
          if (selectedSiteId) olvidarSeleccion();
        } else {
          // cualquier OTRO 403 o 5xx sigue siendo un error
          setError(e?.message ?? 'Error cargando sitios');
        }
      } finally {
        setLoading(false);
      }
    },
    [selectedSiteId, tenantId, olvidarSeleccion],
  );

  const refresh = useCallback(() => cargar(false), [cargar]);

  useEffect(() => {
    if (enabled) {
      void cargar(true);
    } else {
      setSites([]);
      setError(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const selectSite = useCallback((siteId: string | null) => {
    setSelectedSiteId(siteId);
    try {
      if (siteId) {
        localStorage.setItem(LOCAL_STORAGE_KEY, siteId);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    } catch {
      // ignore localStorage errors (private mode)
    }
  }, []);

  const selectedSite = selectedSiteId
    ? sites.find((s) => s.id === selectedSiteId) ?? null
    : null;

  return (
    <SelectedSiteContext.Provider
      value={{
        sites,
        selectedSiteId,
        selectedSite,
        loading,
        error,
        refresh,
        selectSite,
      }}
    >
      {children}
    </SelectedSiteContext.Provider>
  );
};

export const useSelectedSite = (): SelectedSiteContextValue => {
  const ctx = useContext(SelectedSiteContext);
  if (!ctx) {
    throw new Error('useSelectedSite must be used within SelectedSiteProvider');
  }
  return ctx;
};
